import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import {
  ClipboardCheckIcon,
  MessageDotsCircleIcon,
  AnnotationIcon,
  Settings01Icon,
  CheckVerified01Icon,
} from '@untitledui-icons/react/line'
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from '@/components/application/side-panel'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { AudioPlayer } from '@/components/data/AudioPlayer'
import { useConversation } from '@/hooks/api/useConversations'
import { useAssistants, useAssistant } from '@/hooks/api/useAssistants'
import {
  usePartnerReview,
  useSubmitPartnerReview,
  type SubmitPartnerReviewInput,
} from '@/hooks/api/usePartnerReview'
import { usePermissions } from '@/hooks/api'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/lib/api'
import { toast } from '@/lib/toast'
import { ConversationDetails } from './ConversationDetails'
import { ConversationTranscript } from './ConversationTranscript'
import { ConversationNotes } from './ConversationNotes'
import { ConversationAdmin } from './ConversationAdmin'
import { ConversationQALabel } from './ConversationQALabel'

interface ConversationDrawerProps {
  conversationId: string | number | null
  onClose: () => void
  /** Optional callback for tab changes. If not provided, navigates to /conversations/:id/:tab */
  onTabChange?: (tabId: string) => void
  /** Initial tab to show (for query param based routing). Falls back to URL param or 'overview' */
  initialTab?: string
}

type TabId = 'overview' | 'transcript' | 'notes' | 'qa' | 'admin'

const tabConfig = [
  { id: 'overview' as const, labelKey: 'overview', icon: ClipboardCheckIcon },
  { id: 'transcript' as const, labelKey: 'transcript', icon: MessageDotsCircleIcon },
  { id: 'notes' as const, labelKey: 'notes', icon: AnnotationIcon },
  { id: 'qa' as const, labelKey: 'qa', icon: CheckVerified01Icon },
  { id: 'admin' as const, labelKey: 'admin', icon: Settings01Icon },
]

export function ConversationDrawer({
  conversationId,
  onClose,
  onTabChange,
  initialTab,
}: ConversationDrawerProps) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { tab: tabParam } = useParams<{ id: string; tab: string }>()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const token = useAuthStore((state) => state.accessToken)

  // Translate tab labels
  const tabs = tabConfig.map((tab) => ({
    ...tab,
    label: t(`resources.conversations.tabs.${tab.labelKey}`),
  }))

  // Use initialTab prop (for query param routing), then URL param, then default to 'overview'
  const selectedTab = (initialTab as TabId) || (tabParam as TabId) || 'overview'

  const { data: conversation, isLoading } = useConversation(
    conversationId ?? undefined
  )
  const { data: assistants = [] } = useAssistants()

  // Find assistant from list for basic info (name, timezone)
  const assistantFromList = useMemo(() => {
    if (!conversation?.assistant_id || !assistants.length) return undefined
    return assistants.find((a) => a.id === conversation.assistant_id)
  }, [conversation?.assistant_id, assistants])

  // Fetch the full assistant (with config) for screenshot support
  const { data: assistantFull } = useAssistant(
    conversation?.assistant_id,
    { enabled: Boolean(conversation?.assistant_id) }
  )

  // Use full assistant data if available, otherwise fall back to list data
  const assistant = assistantFull || assistantFromList
  const assistantName = assistant?.name
  const assistantTimezone = assistant?.timezone

  // Parse meta for recording URL
  const recordingUrl = useMemo(() => {
    if (!conversation?.meta) return null
    try {
      const meta = JSON.parse(conversation.meta)
      if (meta.recording_url && token) {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
        return `${apiUrl}/conversations/${conversation.id}/recording.wav?access_token=${token}`
      }
    } catch {
      return null
    }
    return null
  }, [conversation?.meta, conversation?.id, token])

  // Check if user can see admin/QA tabs
  const canSeeAdmin = can('access:admin-panel') || can('view:partners')
  const canSeeQA = can('access:admin-panel')
  const canReview = can('access:admin-panel')

  // Per-turn partner review (admin-only); backend decides whether the conversation qualifies.
  // Fetched as soon as the drawer opens so the transcript tab can show a reviewable indicator.
  const { data: partnerReview } = usePartnerReview(conversation?.id, {
    enabled: canReview,
  })
  const submitPartnerReview = useSubmitPartnerReview(conversation?.id)

  const handleSubmitReview = async (input: SubmitPartnerReviewInput) => {
    try {
      await submitPartnerReview.mutateAsync(input)
      toast.success(t('resources.conversations.transcript.review.saved'))
    } catch (error) {
      toast.error(getErrorMessage(error))
      throw error
    }
  }

  const transcriptReview =
    canReview && partnerReview?.reviewable
      ? {
          reviewableIndices: partnerReview.reviewable_indices,
          reviews: partnerReview.reviews,
          onSubmit: handleSubmitReview,
        }
      : undefined

  // Filter tabs based on permissions
  const visibleTabs = tabs.filter((tab) => {
    if (tab.id === 'admin') {
      return canSeeAdmin && conversation?.meta
    }
    if (tab.id === 'qa') {
      return canSeeQA
    }
    return true
  })

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    if (!conversationId) return
    
    // If parent provided a custom tab change handler, use it
    if (onTabChange) {
      onTabChange(tabId)
      return
    }
    
    // Default behavior: navigate to /conversations/:id/:tab
    const queryString = searchParams.toString()
    navigate(`/conversations/${conversationId}/${tabId}${queryString ? `?${queryString}` : ''}`, { replace: true })
  }

  // Keyboard navigation for left/right arrows
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if user is typing in an input
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Tab switching with [ and ]
      if (e.key === '[' || e.key === ']') {
        const currentIndex = visibleTabs.findIndex((t) => t.id === selectedTab)
        const newIndex =
          e.key === '['
            ? Math.max(0, currentIndex - 1)
            : Math.min(visibleTabs.length - 1, currentIndex + 1)
        handleTabChange(visibleTabs[newIndex].id)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedTab, visibleTabs])

  return (
    <SidePanel
      isOpen={Boolean(conversationId)}
      onClose={onClose}
      side="right"
      size="full"
      variant="details"
    >
      <SidePanelContent>
        <SidePanelHeader
          title={assistantName
            ? t('resources.conversations.drawer.conversation_with', { name: assistantName })
            : t('resources.conversations.drawer.title')}
          onClose={onClose}
        />

        {isLoading ? (
          <div className="flex flex-1 items-start justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
            <span className="sr-only">{t('resources.generic.loading')}</span>
          </div>
        ) : conversation ? (
          <>
            {/* Audio Player */}
            <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              {recordingUrl ? (
                <AudioPlayer src={recordingUrl} />
              ) : (
                <div className="flex items-center justify-center rounded-lg bg-gray-50 py-3 dark:bg-gray-800">
                  <span className="text-sm text-gray-500">
                    {t('resources.conversations.no_recording')}
                  </span>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 px-6 dark:border-gray-700">
              <Tabs
                selectedKey={selectedTab}
                onSelectionChange={(key) => handleTabChange(key as string)}
              >
                <TabsList>
                  {visibleTabs.map((tab) => (
                    <TabsTrigger key={tab.id} id={tab.id}>
                      <div className="flex items-center gap-1.5">
                        <tab.icon className="h-4 w-4" />
                        {tab.label}
                        {tab.id === 'transcript' && partnerReview?.reviewable && (
                          <span
                            className="h-1.5 w-1.5 rounded-full bg-brand-500"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {/* Tab Content */}
            <SidePanelBody>
              {selectedTab === 'overview' && (
                <ConversationDetails
                  conversation={conversation}
                  assistantId={assistant?.id}
                  assistantName={assistantName}
                  assistantTimezone={assistantTimezone}
                />
              )}
              {selectedTab === 'transcript' && (
                <ConversationTranscript
                  key={conversation.id}
                  messages={conversation.messages}
                  assistantConfig={assistant?.config}
                  conversationDate={conversation.created_at}
                  review={transcriptReview}
                />
              )}
              {selectedTab === 'notes' && (
                <ConversationNotes
                  conversationId={conversation.id}
                  customerId={assistant?.customer_id}
                />
              )}
              {selectedTab === 'qa' && canSeeQA && (
                <ConversationQALabel conversation={conversation} />
              )}
              {selectedTab === 'admin' && canSeeAdmin && (
                <ConversationAdmin conversation={conversation} />
              )}
            </SidePanelBody>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <span className="text-gray-500">{t('resources.conversations.not_found')}</span>
          </div>
        )}
      </SidePanelContent>
    </SidePanel>
  )
}

export default ConversationDrawer
